import React, { useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import Select, { components } from "react-select";

export function normalizeNatureSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

function CheckboxOption(props) {
  return (
    <components.Option {...props}>
      <span className="statistics-nature-option">
        <input
          className="statistics-nature-checkbox"
          type="checkbox"
          checked={props.isSelected}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
        />
        <span>{props.children}</span>
      </span>
    </components.Option>
  );
}

const defaultStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderColor: state.isFocused ? "#2585d8" : "var(--border)",
    backgroundColor: "var(--surface-raised)",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(37, 133, 216, 0.15)" : "none",
  }),
  menu: (base) => ({ ...base, zIndex: 25 }),
  menuList: (base) => ({ ...base, maxHeight: 240 }),
  input: (base) => ({ ...base, color: "var(--text)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-muted)" }),
  multiValue: (base) => ({ ...base, backgroundColor: "rgba(37, 133, 216, 0.12)" }),
  multiValueLabel: (base) => ({ ...base, color: "var(--text)" }),
};

export default function NatureMultiSelect({
  inputId,
  ariaLabel = "Natureza",
  options = [],
  value = [],
  onChange,
  placeholder = "Todas as naturezas",
  isLoading = false,
  isDisabled = false,
  isOptionDisabled,
  showActions = true,
  showCheckboxes = true,
  summarizeMultiple = true,
  classNamePrefix = "statistics-nature-select",
  styles = {},
  noOptionsMessage = () => "Nenhuma natureza corresponde à pesquisa.",
  loadingMessage = () => "Carregando naturezas...",
}) {
  const [search, setSearch] = useState("");
  const selected = useMemo(() => {
    const seen = new Set();
    return (value || []).filter((option) => {
      const key = String(option.value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [value]);
  const visibleOptions = useMemo(() => {
    const term = normalizeNatureSearch(search.trim());
    return term
      ? options.filter((option) => normalizeNatureSearch(option.label).includes(term))
      : options;
  }, [options, search]);

  const changeSelection = (nextOptions) => {
    const seen = new Set();
    const uniqueOptions = (nextOptions || []).filter((option) => {
      const key = String(option.value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    onChange?.(uniqueOptions);
  };

  const selectVisible = () => {
    const selectedById = new Map(selected.map((option) => [String(option.value), option]));
    visibleOptions.forEach((option) => selectedById.set(String(option.value), option));
    changeSelection(Array.from(selectedById.values()));
  };

  return (
    <>
      <Select
        inputId={inputId}
        aria-label={ariaLabel}
        classNamePrefix={classNamePrefix}
        value={selected}
        options={options}
        isMulti
        isSearchable
        isLoading={isLoading}
        isDisabled={isDisabled}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        isClearable={false}
        controlShouldRenderValue={!summarizeMultiple || selected.length <= 1}
        placeholder={summarizeMultiple && selected.length > 1
          ? `${selected.length} naturezas selecionadas`
          : placeholder}
        filterOption={({ label }, input) =>
          normalizeNatureSearch(label).includes(normalizeNatureSearch(input))
        }
        onChange={changeSelection}
        onInputChange={(nextValue, { action }) => {
          if (action === "input-change") setSearch(nextValue);
          if (["set-value", "menu-close", "input-blur"].includes(action)) setSearch("");
          return nextValue;
        }}
        noOptionsMessage={noOptionsMessage}
        loadingMessage={loadingMessage}
        menuPlacement="auto"
        maxMenuHeight={240}
        isOptionDisabled={isOptionDisabled}
        components={showCheckboxes ? { Option: CheckboxOption } : undefined}
        styles={{ ...defaultStyles, ...styles }}
      />
      {showActions && (
        <div className="statistics-nature-filter-actions" role="group" aria-label="Ações das naturezas">
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={selectVisible}
            disabled={!visibleOptions.length || isDisabled}
          >
            Selecionar visíveis
          </Button>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={() => changeSelection([])}
            disabled={!selected.length}
          >
            Limpar seleção
          </Button>
        </div>
      )}
    </>
  );
}
